// Central role -> allowed-section access control.
//
// 'Admin'     => full access (every section, current behaviour).
// 'Moderator' => restricted access: only the Subscription page (revenue-free
//                subset of Financials) and the Channel Attribution page.
// 'Marketing' => the Moderator sections WITHOUT the Subscription page.
//
// The backend admin login already returns `user.role` (enum: 'Admin' | 'Moderator' | 'Marketing')
// and carries it inside the JWT, so this map is the single source of truth on the
// frontend for what each role is allowed to see. To add another restricted role,
// add its allowed paths here and (if it should be enforced server-side) mirror the
// prefixes in the backend authorization middleware.

export const ROLES = {
  ADMIN: 'Admin',
  MODERATOR: 'Moderator',
  MARKETING: 'Marketing',
};

// Sentinel meaning "all sections".
const ALL = '*';

// Routes a Moderator may access. Sub-routes are matched by prefix
// (e.g. '/channel-attribution' also grants '/channel-attribution/anything').
// Note: Moderators get '/subscription' — the revenue-free subset of Financials —
// NOT '/financials', which stays Admin-only because it exposes revenue analytics.
const MODERATOR_SECTIONS = [
  '/subscription',
  '/channel-attribution',
  '/attribution-members',
  '/onboarding-dropoff',
];

const MARKETING_SECTIONS = MODERATOR_SECTIONS.filter((p) => p !== '/subscription');

const ROLE_SECTIONS = {
  [ROLES.ADMIN]: ALL,
  [ROLES.MODERATOR]: MODERATOR_SECTIONS,
  [ROLES.MARKETING]: MARKETING_SECTIONS,
};

// Landing route per role (first section they're allowed to see).
const ROLE_DEFAULT_ROUTE = {
  [ROLES.ADMIN]: '/',
  [ROLES.MODERATOR]: '/subscription',
  [ROLES.MARKETING]: '/channel-attribution',
};

/**
 * Whether the given role may access the given route path.
 *
 * Fail-open on an unknown/missing role: pre-existing admin sessions created
 * before roles were stored have no role and are treated as full-access Admins.
 * The backend independently enforces the real restriction for Moderator tokens,
 * so this frontend fallback only affects what the UI hides, not data security.
 */
export const hasSectionAccess = (role, path) => {
  const allowed = ROLE_SECTIONS[role];
  if (allowed === undefined || allowed === ALL) return true;
  return allowed.some((p) => path === p || path.startsWith(`${p}/`));
};

/** Route to send a role to when it has no access to the requested page. */
export const getDefaultRoute = (role) => ROLE_DEFAULT_ROUTE[role] || '/';

/**
 * Whether a role may perform user-management write actions (suspend, hide from
 * search, shadow hide, demote, send message, verification approve/reject, ...).
 * Read-only roles like Moderator and Marketing only get the "View" action in the Actions menu.
 *
 * Fail-open on unknown/missing role (treated as full-access Admin), matching
 * hasSectionAccess. This only governs what the UI offers; the backend
 * roleAccessMiddleware independently rejects any non-GET request from those roles.
 */
export const canManageUsers = (role) => role !== ROLES.MODERATOR && role !== ROLES.MARKETING;
